/* eslint-disable import/no-extraneous-dependencies */
import { useContext, useEffect, useMemo } from "react";
import {
  useGetUsersByRole2,
  useGetAllUsers,
  useGetUsersByRole,
  useGetVehicles,
  useGetAllFiledTeams,
} from "utils/apis/lookups.api";
import { TimePicker } from "@progress/kendo-react-dateinputs";
import {
  useEditDailyOperationRoutes,
  useEditRoute,
} from "utils/apis/rotes.api";
import {
  Button,
  Combobox,
  Input,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Textarea,
  Form,
} from "@repo/ui";
import { useForm } from "react-hook-form";
import moment from "moment";
import { UserContext } from "utils/reducers/user";

const currentDate = new Date();

const defaultValues = {
  routeNo: "",
  busTime: new Date(),
  routeTime: new Date(),
  teamLeaderId: "",
  driverId: "",
  guard1Id: "",
  guard2Id: "",
  guard3Id: "",
  vanRegistrationNo: "",
  cashBoxSerial: "",
  hhtSerial: "",
  printerSerial: "",
  requestedBy: "",
  comments: "",
};

function EditRoute({ route, onRouteUpdated }) {
  const {
    mutate: editRoute,
    isSuccess: editSuccess,
    isError: editError,
  } = useEditDailyOperationRoutes();
  const user = useContext(UserContext);

  const { data: teamLeaders } = useGetUsersByRole(
    user.CashCenterId,
    "Team Leader",
  );
  const { data: fieldTeam } = useGetAllFiledTeams(user.CashCenterId, "Guard");
  const { data: drivers } = useGetUsersByRole(user.CashCenterId, "Driver");
  const { data: users } = useGetAllUsers();
  const { data: vehicles } = useGetVehicles(user.CashCenterId);
  let _busTime = "";
  let _routeTime = "";
  if (route?.busTime) {
    const _time = route.busTime.split("T").join(" ").replace("Z", "");

    _busTime = moment(_time, "YYYY-MM-DD HH:mm:ss").toDate();
  }

  if (route?.routeTime) {
    const _time = route.routeTime.split("T").join(" ").replace("Z", "");
    _routeTime = moment(_time, "YYYY-MM-DD HH:mm:ss").toDate();
  }

  const _guards = route?.fieldTeam.filter((elm) => elm.role === "Guard") || [];
  const teamMembers = {
    teamLeaderId:
      route?.fieldTeam.find((elm) => elm.role === "Team Leader")?.employeeId ||
      "",
    driverId:
      route?.fieldTeam.find((elm) => elm.role === "Driver")?.employeeId || "",
    guard1Id: _guards?.length ? _guards[0].employeeId : "",
    guard2Id: _guards?.length > 1 ? _guards[1].employeeId : "",
    guard3: _guards?.length > 2 ? _guards[2].employeeId : "",
  };
  const _routeDefault = {
    ...defaultValues,
    ...route,
    busTime: route?.busTime ? moment(_busTime).toDate() : null,
    routeTime: route?.routeTime ? moment(_routeTime).toDate() : null,
    ...teamMembers,
  };
  const form = useForm({
    defaultValues: { ..._routeDefault },
  });

  const onBtnUpdateRouteClicked = () => {
    const _data = form.getValues();
    let busTime = null;
    if (_data.busTime) {
      busTime = moment(_data.busTime).format("[1001-01-01T]HH:mm:ss[Z]");
    }
    let routeTime = null;
    if (_data.routeTime) {
      routeTime = moment(_data.routeTime).format("[1001-01-01T]HH:mm:ss[Z]");
    }
    editRoute({
      routeNo: _data.routeNo,
      // Ensure these are Date objects if your API/Function requires them
      busTime,
      routeTime,
      // Mapping personnel (assuming the form stores the ID or Name)
      teamLeaderId: _data.teamLeaderId,
      driverId: _data.driverId,
      guard1Id: _data.guard1Id,
      guard2Id: _data.guard2Id,
      guard3Id: _data.guard3Id,
      vanRegistrationNo: _data.vanRegistrationNo,
      cashBoxSerial: _data.cashBoxSerial,
      hhtSerial: _data.hhtSerial,
      printerSerial: _data.printerSerial,
      requestedBy: _data.requestedBy,
      comments: _data.comments,
    });
  };
  useEffect(() => {
    if (editSuccess) {
      onRouteUpdated();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editSuccess, editError]);
  const editPermissions = {
    "Operations Supervisor": [
      "busTime",
      "routeTime",
      "teamLeaderId",
      "driverId",
      "guard1Id",
      "guard2Id",
      "guard3Id",
      "cashBoxSerial",
      "comments",
    ],
    "PA Assistant": ["All"],
    "PA Supervisor": ["All"],
    "Fleet Officer": ["vanRegistrationNo"],
  };
  const editableFields = useMemo(() => {
    const allowedUsers = Object.keys(editPermissions);
    let allowedFields = [];

    allowedUsers.forEach((elm) => {
      if (user.Roles.includes(elm)) {
        allowedFields = editPermissions[elm];
      }
    });

    return allowedFields;
  }, [user]);

  const isFieldEditable = (field) => {
    return editableFields.includes(field) || editableFields.includes("All");
  };

  return (
    <>
      <Form {...form}>
        <div className="flex flex-row gap-2">
          <div className="w-full">
            <FormInput form={form} name="routeNo" label="Route No" disabled />
          </div>
          <div className="w-full">
            <FormInput form={form} name="area" label="Area" disabled />
          </div>
          <div className="w-full">
            <FormInput form={form} name="region" label="Region" disabled />
          </div>
          <div className="w-full">
            <FormInput form={form} name="status" label="Status" disabled />
          </div>
        </div>

        <div className="flex flex-row gap-2 mt-2">
          <div className="w-full">
            <FormCombobox
              form={form}
              label="Vehicle"
              name="vanRegistrationNo"
              placeholder="Select Vehicle"
              data={
                vehicles?.map((item) => ({
                  label: item.registrationNo,
                  value: item.registrationNo,
                })) || []
              }
              disabled={!isFieldEditable("vanRegistrationNo")}
            />
          </div>
          <div className="w-full">
            <FormInput
              form={form}
              name="cashBoxSerial"
              label="Cash Box Serial"
              disabled={!isFieldEditable("cashBoxSerial")}
            />
          </div>
          <div className="w-full">
            <FormInput
              form={form}
              name="hhtSerial"
              label="HHT Serial"
              disabled={!isFieldEditable("hhtSerial")}
            />
          </div>
          <div className="w-full">
            <FormInput
              form={form}
              name="printerSerial"
              label="printer Serial"
              disabled={!isFieldEditable("printerSerial")}
            />
          </div>
        </div>
        <div className="flex flex-row gap-2 mt-2">
          <div className="w-full">
            <FormCombobox
              form={form}
              name="teamLeaderId"
              label="Team Leader"
              disabled={!isFieldEditable("teamLeaderId")}
              data={fieldTeam?.map((elm) => ({
                label: `${elm.name}-${elm.employeeId}-${elm.nationality}-${elm.designation || ""}`,
                value: elm.employeeId,
              }))}
            />
          </div>
          <div className="w-full">
            <FormCombobox
              form={form}
              name="driverId"
              label="Driver"
              disabled={!isFieldEditable("driverId")}
              data={fieldTeam?.map((elm) => ({
                label: `${elm.name}-${elm.employeeId}-${elm.nationality}-${elm.designation || ""}`,
                value: elm.employeeId,
              }))}
            />
          </div>
          <div className="w-full">
            <FormCombobox
              form={form}
              name="guard1Id"
              label="Custodian"
              disabled={!isFieldEditable("guard1Id")}
              data={fieldTeam?.map((elm) => ({
                label: `${elm.name}-${elm.employeeId}-${elm.nationality}-${elm.designation || ""}`,
                value: elm.employeeId,
              }))}
            />
          </div>
          <div className="w-full">
            <FormCombobox
              form={form}
              name="guard2Id"
              label="Guard 1"
              disabled={!isFieldEditable("guard2Id")}
              data={fieldTeam?.map((elm) => ({
                label: `${elm.name}-${elm.employeeId}-${elm.nationality}-${elm.designation || ""}`,
                value: elm.employeeId,
              }))}
            />
          </div>
        </div>

        <div className="flex flex-row gap-2 mt-2">
          <div className="w-full">
            <FormCombobox
              form={form}
              name="guard3Id"
              label="Guard 2"
              disabled={!isFieldEditable("guard3Id")}
              data={fieldTeam?.map((elm) => ({
                label: `${elm.name}-${elm.employeeId}-${elm.nationality}-${elm.designation || ""}`,
                value: elm.employeeId,
              }))}
            />
          </div>
          <div className="w-full">
            <FormCombobox
              form={form}
              name="requestedBy"
              label="RequestedBy"
              disabled={!isFieldEditable("requestedBy")}
              data={users?.map((elm) => ({
                label: `${elm.name}-${elm.employeeId}`,
                value: elm.employeeId,
              }))}
            />
          </div>
          <div className="w-full">
            <FormField
              control={form.control}
              name="busTime"
              rules={{ required: "Please select time" }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Bus Time</FormLabel>
                  <TimePicker
                    value={field.value}
                    disabled={!isFieldEditable("busTime")}
                    onChange={(e) => {
                      field.onChange(e?.value || null);
                    }}
                    format="HH:mm" // Use "hh:mm a" for 12-hour clock
                  />
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div className="w-full">
            <FormField
              control={form.control}
              name="routeTime"
              rules={{ required: "Please select time" }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Route Time</FormLabel>
                  <TimePicker
                    value={field.value}
                    disabled={!isFieldEditable("routeTime")}
                    onChange={(e) => {
                      field.onChange(e?.value || null);
                    }}
                    format="HH:mm" // Use "hh:mm a" for 12-hour clock
                  />
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>
        <div className="w-full ">
          <FormField
            control={form.control}
            name="comments"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Comments</FormLabel>
                <Textarea
                  className="w-full"
                  value={field.value}
                  disabled={!isFieldEditable("comments")}
                  onChange={(e) => {
                    field.onChange(e);
                  }}
                />
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </Form>

      <div className="border-b my-3 bg-accent" />
      <Button
        variant="secondary"
        onClick={onBtnUpdateRouteClicked}
        className="ml-auto flex w-[140px]"
      >
        Update Route
      </Button>
    </>
  );
}

export default EditRoute;
function FormCombobox({
  form,
  name,
  label,
  placeholder,
  data,
  rules,
  onChange,
  disabled,
  ...props
}) {
  return (
    <FormField
      control={form.control}
      name={name}
      rules={rules}
      render={({ field }) => (
        <FormItem>
          <FormLabel>{label}</FormLabel>
          <Combobox
            value={field.value}
            setValue={(e) => {
              field.onChange(e);
              onChange?.(e);
            }}
            data={data}
            placeholder={placeholder}
            disabled={disabled}
            {...props}
          />
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

function FormInput({
  form,
  name,
  label,
  placeholder,
  rules,
  data,
  onChange,
  disabled,
  ...props
}) {
  return (
    <FormField
      control={form.control}
      name={name}
      rules={rules}
      render={({ field }) => (
        <FormItem>
          <FormLabel>{label}</FormLabel>
          <Input
            value={field.value}
            placeholder={placeholder}
            onChange={(e) => {
              field.onChange(e);
              onChange?.(e);
            }}
            disabled={disabled}
            {...props}
          />
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
